import { useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Copy,
  GripVertical,
  Italic,
  Link2,
  Trash2,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { cn } from '../../lib/cn';
import { avvolgiSelezione, paragrafiInTesto, testoInParagrafi } from '../../lib/email/testoRicco';
import { MergeTagPicker } from './MergeTagPicker';
import { SceltaEvento, STILE_TENDINA } from './SceltaEvento';
import type { Allineamento, Blocco, BloccoBottone, PaginaEvento } from '@email/types.ts';

interface BlockCardProps {
  blocco: Blocco;
  onChange: (blocco: Blocco) => void;
  onDuplica: () => void;
  onElimina: () => void;
}

const ETICHETTE: Record<Blocco['tipo'], string> = {
  titolo: 'Titolo',
  testo: 'Testo',
  immagine: 'Immagine',
  bottone: 'Bottone',
  separatore: 'Riga divisoria',
  spazio: 'Spazio vuoto',
  percorso_evento: "Percorso dell'evento",
};

const PAGINE_EVENTO: { valore: PaginaEvento; etichetta: string }[] = [
  { valore: 'scelta', etichetta: 'Pagina di scelta del percorso' },
  { valore: 'app', etichetta: "App dell'evento" },
  { valore: 'registrazione', etichetta: 'Registrazione dei biglietti (per chi ha comprato)' },
];

/** Avviso per i blocchi che dipendono da un evento non ancora scelto. */
function SenzaEvento({ cosa }: { cosa: string }) {
  return (
    <p className="text-[11px] text-amber-700">
      Scegli l'evento: senza, {cosa} non compare nella mail.
    </p>
  );
}

/**
 * Dove porta un bottone: un indirizzo uguale per tutti, oppure il link
 * personale di un evento, diverso per ogni persona (lo scrive il motore al
 * momento dell'invio, per questo l'indirizzo qui non si vede).
 */
function DestinazioneBottone({
  blocco,
  onChange,
}: {
  blocco: BloccoBottone;
  onChange: (blocco: Blocco) => void;
}) {
  const personale = blocco.personale;

  const scegliTipo = (tipo: string) => {
    if (tipo === 'personale' && !personale) {
      onChange({ ...blocco, url: '', personale: { evento_id: '', pagina: 'scelta' } });
    } else if (tipo === 'fisso' && personale) {
      onChange({ ...blocco, url: '', personale: undefined });
    }
  };

  return (
    <>
      <div>
        <Label className="text-xs">Dove porta</Label>
        <select
          value={personale ? 'personale' : 'fisso'}
          onChange={(e) => scegliTipo(e.target.value)}
          className={STILE_TENDINA}
        >
          <option value="fisso">Indirizzo fisso</option>
          <option value="personale">Link personale dell'evento</option>
        </select>
      </div>

      {!personale && (
        <div>
          <Label className="text-xs">Indirizzo</Label>
          <Input
            value={blocco.url}
            onChange={(e) => onChange({ ...blocco, url: e.target.value })}
            placeholder="https://…"
            className="mt-1"
          />
        </div>
      )}

      {personale && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Evento</Label>
              <SceltaEvento
                valore={personale.evento_id}
                onChange={(id) => onChange({ ...blocco, personale: { ...personale, evento_id: id } })}
              />
            </div>
            <div>
              <Label className="text-xs">Quale pagina</Label>
              <select
                value={personale.pagina}
                onChange={(e) =>
                  onChange({ ...blocco, personale: { ...personale, pagina: e.target.value as PaginaEvento } })
                }
                className={STILE_TENDINA}
              >
                {PAGINE_EVENTO.map((p) => (
                  <option key={p.valore} value={p.valore}>
                    {p.etichetta}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {!personale.evento_id && <SenzaEvento cosa="il bottone" />}
          {personale.pagina === 'registrazione' ? (
            <p className="text-[11px] text-neutral-400">
              Chi ha comprato biglietti sul sito riceve il link alla pagina dei suoi biglietti, dove registra
              chi li usa. A chi non ne ha comprati il bottone non arriva. Nell'anteprima il bottone non porta
              da nessuna parte.
            </p>
          ) : (
            <p className="text-[11px] text-neutral-400">
              Ognuno riceve il suo link: la pagina lo riconosce senza chiedergli niente. Vale per chi e'
              nella lista dell'evento; agli altri arriva il link alla pagina dell'evento, senza i dati
              personali. Nell'anteprima il bottone non porta da nessuna parte.
            </p>
          )}
        </>
      )}
    </>
  );
}

const ALLINEAMENTI: { valore: Allineamento; icona: typeof AlignLeft; titolo: string }[] = [
  { valore: 'sinistra', icona: AlignLeft, titolo: 'A sinistra' },
  { valore: 'centro', icona: AlignCenter, titolo: 'Al centro' },
  { valore: 'destra', icona: AlignRight, titolo: 'A destra' },
];

function SceltaAllineamento({
  valore,
  onChange,
}: {
  valore: Allineamento;
  onChange: (v: Allineamento) => void;
}) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
      {ALLINEAMENTI.map(({ valore: v, icona: Icona, titolo }) => (
        <button
          key={v}
          type="button"
          title={titolo}
          onClick={() => onChange(v)}
          className={cn(
            'px-2 py-1.5 transition-colors',
            valore === v ? 'bg-primary-600 text-white' : 'bg-white text-neutral-500 hover:bg-gray-50'
          )}
        >
          <Icona className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export function BlockCard({ blocco, onChange, onDuplica, onElimina }: BlockCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: blocco.id,
  });
  const areaTesto = useRef<HTMLTextAreaElement>(null);

  // Il testo mostrato nella casella e' tenuto qui, non ricalcolato dai
  // paragrafi ad ogni tasto: paragrafi->testo pareggia gli spazi (trim), e se
  // lo si rifà dopo ogni carattere lo spazio appena scritto in fondo alla
  // riga sparisce subito, sembrando che il tasto spazio non funzioni.
  const [testoGrezzo, setTestoGrezzo] = useState(() =>
    blocco.tipo === 'testo' ? paragrafiInTesto(blocco.paragrafi) : ''
  );

  const stile = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const aggiornaTesto = (nuovoTesto: string) => {
    if (blocco.tipo !== 'testo') return;
    setTestoGrezzo(nuovoTesto);
    onChange({ ...blocco, paragrafi: testoInParagrafi(nuovoTesto) });
  };

  /** Applica grassetto o corsivo alla parte selezionata nella casella. */
  const formatta = (simbolo: string) => {
    const area = areaTesto.current;
    if (!area || blocco.tipo !== 'testo') return;

    const esito = avvolgiSelezione(testoGrezzo, area.selectionStart, area.selectionEnd, simbolo);
    aggiornaTesto(esito.testo);

    // Il cursore va rimesso dove l'utente se lo aspetta, dopo il ridisegno.
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(esito.inizio, esito.fine);
    });
  };

  const inserisci = (frammento: string) => {
    const area = areaTesto.current;
    if (!area || blocco.tipo !== 'testo') return;

    const posizione = area.selectionStart;
    const nuovo = testoGrezzo.slice(0, posizione) + frammento + testoGrezzo.slice(area.selectionEnd);
    aggiornaTesto(nuovo);

    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(posizione + frammento.length, posizione + frammento.length);
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={stile}
      className={cn(
        'group rounded-lg border bg-white transition-colors',
        isDragging ? 'border-primary-400 shadow-lg' : 'border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/70 rounded-t-lg">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600 touch-none"
          title="Trascina per spostare"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <span className="text-xs font-medium text-neutral-600 flex-1">{ETICHETTE[blocco.tipo]}</span>
        <button
          type="button"
          onClick={onDuplica}
          title="Duplica"
          className="p-1 text-neutral-400 hover:text-neutral-700"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onElimina}
          title="Elimina"
          className="p-1 text-neutral-400 hover:text-red-600"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="p-3 space-y-3">
        {blocco.tipo === 'titolo' && (
          <>
            <Input
              value={blocco.testo}
              onChange={(e) => onChange({ ...blocco, testo: e.target.value })}
              placeholder="Scrivi il titolo"
              className="text-lg font-bold"
            />
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                {([1, 2, 3] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => onChange({ ...blocco, livello: l })}
                    className={cn(
                      'px-2.5 py-1.5 text-xs font-semibold transition-colors',
                      blocco.livello === l
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-neutral-500 hover:bg-gray-50'
                    )}
                    title={l === 1 ? 'Titolo grande' : l === 2 ? 'Titolo medio' : 'Titolo piccolo'}
                  >
                    T{l}
                  </button>
                ))}
              </div>
              <SceltaAllineamento
                valore={blocco.allineamento}
                onChange={(v) => onChange({ ...blocco, allineamento: v })}
              />
            </div>
          </>
        )}

        {blocco.tipo === 'testo' && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => formatta('*')}
                  title="Grassetto"
                  className="px-2.5 py-1.5 bg-white text-neutral-600 hover:bg-gray-50"
                >
                  <Bold className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => formatta('_')}
                  title="Corsivo"
                  className="px-2.5 py-1.5 bg-white text-neutral-600 hover:bg-gray-50 border-l border-gray-200"
                >
                  <Italic className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => inserisci('[scrivi qui](https://)')}
                  title="Inserisci un link"
                  className="px-2.5 py-1.5 bg-white text-neutral-600 hover:bg-gray-50 border-l border-gray-200"
                >
                  <Link2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <MergeTagPicker onScelto={(tag) => inserisci(tag)} />
              <SceltaAllineamento
                valore={blocco.allineamento}
                onChange={(v) => onChange({ ...blocco, allineamento: v })}
              />
            </div>
            <Textarea
              ref={areaTesto}
              rows={5}
              value={testoGrezzo}
              onChange={(e) => aggiornaTesto(e.target.value)}
              placeholder="Scrivi il testo. Lascia una riga vuota per andare a capo con un nuovo paragrafo."
              className="text-sm"
            />
            <p className="text-[11px] text-neutral-400">
              Seleziona una parte di testo e premi i bottoni per formattarla.
            </p>
          </>
        )}

        {blocco.tipo === 'immagine' && (
          <>
            <div>
              <Label className="text-xs">Indirizzo dell'immagine</Label>
              <Input
                value={blocco.url}
                onChange={(e) => onChange({ ...blocco, url: e.target.value })}
                placeholder="https://…"
                className="mt-1"
              />
              <p className="text-[11px] text-neutral-400 mt-1">
                Dev'essere un'immagine gia' pubblicata su internet: chi riceve la mail la scarica da li'.
              </p>
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Input
                value={blocco.alt}
                onChange={(e) => onChange({ ...blocco, alt: e.target.value })}
                placeholder="Cosa si vede nell'immagine"
                className="mt-1"
              />
              <p className="text-[11px] text-neutral-400 mt-1">
                Si legge al posto dell'immagine quando il programma di posta non la scarica, e capita spesso.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                {(['piena', 'meta', 'piccola'] as const).map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => onChange({ ...blocco, larghezza: l })}
                    className={cn(
                      'px-2.5 py-1.5 text-xs transition-colors',
                      blocco.larghezza === l
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-neutral-500 hover:bg-gray-50'
                    )}
                  >
                    {l === 'piena' ? 'Larga' : l === 'meta' ? 'Meta' : 'Piccola'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Dove porta se ci cliccano (facoltativo)</Label>
              <Input
                value={blocco.link ?? ''}
                onChange={(e) => onChange({ ...blocco, link: e.target.value || undefined })}
                placeholder="https://…"
                className="mt-1"
              />
            </div>
          </>
        )}

        {blocco.tipo === 'bottone' && (
          <>
            <div>
              <Label className="text-xs">Cosa c'e' scritto sopra</Label>
              <Input
                value={blocco.testo}
                onChange={(e) => onChange({ ...blocco, testo: e.target.value })}
                placeholder="Scopri l'offerta"
                className="mt-1"
              />
            </div>
            <DestinazioneBottone blocco={blocco} onChange={onChange} />
            <SceltaAllineamento
              valore={blocco.allineamento}
              onChange={(v) => onChange({ ...blocco, allineamento: v })}
            />
            <p className="text-[11px] text-neutral-400">
              I clic sul bottone vengono conteggiati: e' il dato piu' affidabile per capire chi e'
              davvero interessato.
            </p>
          </>
        )}

        {blocco.tipo === 'percorso_evento' && (
          <>
            <div>
              <Label className="text-xs">Evento</Label>
              <SceltaEvento
                valore={blocco.evento_id}
                onChange={(id) => onChange({ ...blocco, evento_id: id })}
              />
            </div>
            {!blocco.evento_id && <SenzaEvento cosa="il percorso" />}
            <p className="text-[11px] text-neutral-400 leading-relaxed">
              Ognuno riceve l'elenco dei suoi incontri: giorno, orario, aula e titolo, cosi' come
              sono stati assegnati e confermati nel tabellone dell'evento. Chi non ha ancora un
              percorso confermato legge che non e' ancora pronto. Nell'anteprima si vedono due righe
              di esempio.
            </p>
          </>
        )}

        {blocco.tipo === 'separatore' && (
          <div className="py-2">
            <div className="h-px bg-gray-200" />
          </div>
        )}

        {blocco.tipo === 'spazio' && (
          <div className="flex items-center gap-3">
            <Label className="text-xs whitespace-nowrap">Altezza</Label>
            <input
              type="range"
              min={8}
              max={80}
              step={8}
              value={blocco.altezza}
              onChange={(e) => onChange({ ...blocco, altezza: Number(e.target.value) })}
              className="flex-1 accent-primary-600"
            />
            <span className="text-xs text-neutral-500 w-12 text-right">{blocco.altezza} px</span>
          </div>
        )}
      </div>
    </div>
  );
}
